import { Alert } from 'react-native';
import ReactNativeBlobUtil from 'react-native-blob-util';
import type { TFunction } from 'i18next';
import type {
  AttachmentFile,
  AttachmentEntitySource,
} from 'types/attachmentTypes';
import type {
  AttachmentPolicy,
  SalesOrderAttachmentMeta,
} from 'types/generatedSchemaTypes';
import { isWeb } from 'utils/deviceUtils';

export const MAX_SIZE_BYTES = 5 * 1024 * 1024;
export const MAX_FILE_SIZE_MB = 5;
export const MAX_ATTACHMENTS = 5;

export const normalizeMimeType = (mime?: string) => {
  if (!mime) return 'application/octet-stream';
  if (mime === 'image/jpg') return 'image/jpeg';
  return mime;
};
export const ALLOWED_MIME = new Set([
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
]);

export const validateAttachment = (
  file: AttachmentFile,
  t: TFunction
): boolean => {
  if (file.size && file.size > MAX_SIZE_BYTES) {
    Alert.alert(
      t('longTermParking.attachment.fileTooLargeTitle'),
      `${t('longTermParking.attachment.fileTooLargeText')} ${MAX_FILE_SIZE_MB} MB`
    );
    return false;
  }

  if (file.type && !ALLOWED_MIME.has(file.type)) {
    Alert.alert(
      `${t('longTermParking.attachment.unsupportedTypeTitle')} ${file.type ?? ''}`,
      t('longTermParking.attachment.unsupportedTypeText')
    );
    return false;
  }
  return true;
};

type AttachmentLike = Pick<
  SalesOrderAttachmentMeta,
  'uid' | 'fileName' | 'mimeType' | 'sizeBytes'
>;

/**
 * Maps server-side attachments (sales-order or waiting-list — both share the
 * same shape) to the AttachmentFile used by the in-app reactive store. Pass an
 * `entitySource` for waiting-list (APPROVAL/ITEM); omit for sales-order.
 */
export const mapAttachmentsToFiles = (
  attachments: AttachmentLike[] | null | undefined,
  entityId: string,
  fallbackName: string,
  entitySource?: AttachmentEntitySource
): AttachmentFile[] => {
  if (!attachments?.length) return [];

  return attachments.map(
    (attachment): AttachmentFile => ({
      id: attachment.uid,
      name: attachment.fileName ?? fallbackName,
      uri: '',
      contextId: entityId,
      uploadUid: attachment.uid,
      uploadFileName: attachment.fileName ?? undefined,
      uploadMimeType: attachment.mimeType ?? undefined,
      uploadSizeBytes: attachment.sizeBytes ?? undefined,
      entityId,
      entitySource,
    })
  );
};

const toLocalFilePath = (uri: string) =>
  decodeURI(uri.replace(/^file:\/\//, ''));

export const revokeAttachmentUri = (uri: string | undefined) => {
  if (!uri) return;
  if (isWeb && uri.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(uri);
    } catch {
      // ignore
    }
    return;
  }
  if (!uri.startsWith('file://')) return;
  ReactNativeBlobUtil.fs.unlink(toLocalFilePath(uri)).catch(() => undefined);
};

export type AttachmentUi = {
  showOnWaitingList: boolean;
  requireOnWaitingList: boolean;
  showOnSalesOrder: boolean;
  requireOnSalesOrder: boolean;
};

const NO_ATTACHMENTS: AttachmentUi = {
  showOnWaitingList: false,
  requireOnWaitingList: false,
  showOnSalesOrder: false,
  requireOnSalesOrder: false,
};

export const resolveAttachmentUi = (
  policy: AttachmentPolicy | null | undefined
): AttachmentUi => {
  switch (policy) {
    case 'OPTIONAL':
      return {
        showOnWaitingList: true,
        requireOnWaitingList: false,
        showOnSalesOrder: true,
        requireOnSalesOrder: false,
      };
    case 'MANDATORY_AT_WAITING_LIST':
      return {
        showOnWaitingList: true,
        requireOnWaitingList: true,
        showOnSalesOrder: true,
        requireOnSalesOrder: true,
      };
    case 'MANDATORY_BEFORE_ACTIVATION':
      return {
        showOnWaitingList: true,
        requireOnWaitingList: false,
        showOnSalesOrder: true,
        requireOnSalesOrder: true,
      };
    case 'NONE':
    default:
      return NO_ATTACHMENTS;
  }
};

export const isRequiredSalesOrderAttachmentMissing = (
  required: boolean | null | undefined,
  uploadedCount: number
): boolean => required === true && uploadedCount < 1;

export const formatFileNameForUI = (name: string, max = 17) => {
  const trimmed = name.trim();
  if (trimmed.length <= max) return trimmed;
  const dot = trimmed.lastIndexOf('.');
  const hasExt = dot > 0 && dot < trimmed.length - 1;
  const ext = hasExt ? trimmed.slice(dot) : '';
  const base = hasExt ? trimmed.slice(0, dot) : trimmed;
  const ellipsis = '…';
  const ideal = max - ext.length - ellipsis.length;
  const keep = Math.min(base.length, Math.max(8, ideal));
  const safeKeep = Math.min(keep, max - ext.length - ellipsis.length);

  if (safeKeep <= 0) {
    return trimmed.slice(0, max - 1) + ellipsis;
  }

  return `${base.slice(0, safeKeep)}${ellipsis}${ext}`;
};
